import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { ArrowDown, ArrowUp, Plus, Trash } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import type {
  CommissionCalculation,
  CommissionRule,
  CommissionStructure,
} from "../../types/commissionStructure";
import { CommissionBreakdown } from "./CommissionBreakdown";
import { commissionPresets } from "./commissionPresets";

export interface SampleEconomics {
  contract: string;
  costs: string;
  credits: string;
  rep_rate: string;
}
export function CommissionStructureEditor({
  initial,
  preview,
  onSave,
  onReset,
  saving,
  saveError,
  previewScope,
  job = false,
}: {
  initial: CommissionStructure;
  previewScope: string;
  preview: (
    structure: CommissionStructure,
    sample: SampleEconomics,
  ) => Promise<CommissionCalculation>;
  onSave: (structure: CommissionStructure) => void;
  onReset?: () => void;
  saving: boolean;
  saveError?: string;
  job?: boolean;
}) {
  const [structure, setStructure] = useState(initial);
  const [sample, setSample] = useState<SampleEconomics>({
    contract: "20000",
    costs: "12000",
    credits: "500",
    rep_rate: "50",
  });
  const [request, setRequest] = useState({ structure, sample });
  useEffect(() => {
    const timer = setTimeout(() => setRequest({ structure, sample }), 250);
    return () => clearTimeout(timer);
  }, [structure, sample]);
  const result = useQuery({
    queryKey: ["commission-preview", previewScope, request],
    queryFn: () => preview(request.structure, request.sample),
    retry: false,
  });
  const pending =
    request.structure !== structure ||
    request.sample !== sample ||
    result.isFetching;
  const changeRule = (index: number, patch: Partial<CommissionRule>) =>
    setStructure((s) => ({
      ...s,
      rules: s.rules.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    }));
  const move = (index: number, direction: number) =>
    setStructure((s) => {
      const rules = [...s.rules];
      [rules[index], rules[index + direction]] = [
        rules[index + direction],
        rules[index],
      ];
      return { ...s, rules };
    });
  return (
    <Stack gap={3}>
      <Box>
        <Stack direction="row" gap={1} alignItems="center">
          <Typography level="title-md">
            Start with a familiar structure
          </Typography>
          {JSON.stringify(structure) !== JSON.stringify(initial) && (
            <Chip size="sm" color="warning">
              Unsaved changes
            </Chip>
          )}
        </Stack>
        <Typography level="body-sm" sx={{ mb: 1.5 }}>
          Pick a starting point, then make it yours. Each rule is applied in
          order.
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" },
            gap: 1,
          }}
        >
          {commissionPresets.map((p) => (
            <Button
              key={p.label}
              variant="outlined"
              color="neutral"
              onClick={() => setStructure(structuredClone(p.structure))}
              sx={{
                p: 1.5,
                alignItems: "flex-start",
                textAlign: "left",
                flexDirection: "column",
                gap: 0.5,
                borderColor:
                  structure.name === p.structure.name
                    ? "primary.500"
                    : undefined,
                bgcolor:
                  structure.name === p.structure.name
                    ? "primary.softBg"
                    : undefined,
              }}
            >
              <Typography level="title-sm">{p.label}</Typography>
              <Typography level="body-xs">{p.description}</Typography>
            </Button>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(0, 1.15fr) minmax(320px, 0.85fr)",
          },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Stack gap={2}>
          <FormControl>
            <FormLabel>Structure name</FormLabel>
            <Input
              value={structure.name}
              onChange={(e) =>
                setStructure({ ...structure, name: e.target.value })
              }
              slotProps={{ input: { maxLength: 100 } }}
            />
          </FormControl>
          <Card variant="soft" sx={{ py: 1.5 }}>
            <Stack direction="row" gap={1} alignItems="center">
              <Chip size="sm">Start</Chip>
              <Typography level="title-sm">
                Contract − costs + credits = net profit
              </Typography>
            </Stack>
            <Typography level="body-xs">
              Recorded job costs and credits are included once, before the rules
              below.
            </Typography>
          </Card>
          {structure.rules.map((rule, index) => (
            <Card
              key={index}
              variant="outlined"
              sx={{
                borderLeft: "3px solid",
                borderLeftColor:
                  rule.recipient === "company" ? "primary.400" : "success.400",
                p: 2,
              }}
            >
              <Stack direction="row" gap={1} alignItems="center">
                <Chip
                  size="sm"
                  variant="soft"
                  color={rule.recipient === "company" ? "primary" : "success"}
                >
                  {index + 1}
                </Chip>
                <Input
                  aria-label={`Rule ${index + 1} name`}
                  variant="plain"
                  value={rule.label}
                  onChange={(e) => changeRule(index, { label: e.target.value })}
                  sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}
                  slotProps={{ input: { maxLength: 80 } }}
                />
                <IconButton
                  size="sm"
                  aria-label={`Move rule ${index + 1} up`}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUp />
                </IconButton>
                <IconButton
                  size="sm"
                  aria-label={`Move rule ${index + 1} down`}
                  disabled={index === structure.rules.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDown />
                </IconButton>
                <IconButton
                  size="sm"
                  color="danger"
                  aria-label={`Remove rule ${index + 1}`}
                  onClick={() =>
                    setStructure({
                      ...structure,
                      rules: structure.rules.filter((_, i) => i !== index),
                    })
                  }
                >
                  <Trash />
                </IconButton>
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1.5,
                }}
              >
                <FormControl>
                  <FormLabel>Who receives it?</FormLabel>
                  <Select
                    value={rule.recipient}
                    onChange={(_, v) =>
                      v &&
                      changeRule(index, {
                        recipient: v,
                        ...(v === "company" && rule.method === "rep_rate"
                          ? { method: "percent" as const }
                          : {}),
                      })
                    }
                  >
                    <Option value="company">Company retains</Option>
                    <Option value="rep">Sales rep earns</Option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>How is it calculated?</FormLabel>
                  <Select
                    value={rule.method}
                    onChange={(_, v) => v && changeRule(index, { method: v })}
                  >
                    <Option value="percent">Structure percentage</Option>
                    {rule.recipient === "rep" && (
                      <Option value="rep_rate">Individual rep rate</Option>
                    )}
                    <Option value="fixed">Fixed dollar amount</Option>
                  </Select>
                </FormControl>
                {rule.method !== "rep_rate" && (
                  <FormControl>
                    <FormLabel>
                      {rule.method === "fixed" ? "Amount" : "Percentage"}
                    </FormLabel>
                    <Input
                      type="number"
                      value={rule.value}
                      startDecorator={rule.method === "fixed" ? "$" : undefined}
                      endDecorator={rule.method === "percent" ? "%" : undefined}
                      onChange={(e) =>
                        changeRule(index, { value: e.target.value })
                      }
                      slotProps={{
                        input: {
                          min: 0,
                          max: rule.method === "percent" ? 100 : 9999999999,
                          step: "0.01",
                          "aria-label": `Rule ${index + 1} value`,
                        },
                      }}
                    />
                  </FormControl>
                )}
                {rule.method !== "fixed" && (
                  <FormControl
                    sx={{
                      gridColumn:
                        rule.method === "rep_rate" ? "1 / -1" : undefined,
                    }}
                  >
                    <FormLabel>Applied to</FormLabel>
                    <Select
                      value={rule.basis}
                      onChange={(_, v) => v && changeRule(index, { basis: v })}
                    >
                      <Option value="contract">Gross contract amount</Option>
                      <Option value="profit">
                        Net profit (after costs / credits)
                      </Option>
                      <Option value="remaining">
                        Balance after previous rules
                      </Option>
                    </Select>
                  </FormControl>
                )}
              </Box>
              {rule.method === "rep_rate" && (
                <Typography level="body-xs">
                  Uses the rate saved on the assigned sales rep’s user profile.
                  Individual rates stay editable in Users & permissions.
                </Typography>
              )}
            </Card>
          ))}
          <Button
            variant="outlined"
            color="neutral"
            startDecorator={<Plus />}
            disabled={structure.rules.length >= 20}
            onClick={() =>
              setStructure({
                ...structure,
                rules: [
                  ...structure.rules,
                  {
                    label: "Additional rep bonus",
                    recipient: "rep",
                    method: "fixed",
                    basis: "remaining",
                    value: "250",
                  },
                ],
              })
            }
          >
            Add a rule
          </Button>
          <Card variant="soft">
            <Typography level="title-sm">
              Any balance left goes to the company
            </Typography>
            <Checkbox
              checked={structure.limit_to_profit}
              onChange={(e) =>
                setStructure({
                  ...structure,
                  limit_to_profit: e.target.checked,
                })
              }
              label="Limit each rule to the profit still available"
            />
            <Typography level="body-xs">
              When off, gross-based or fixed commissions may exceed job profit.
              A negative remaining balance is a company loss.
            </Typography>
          </Card>
        </Stack>
        <Card sx={{ position: { lg: "sticky" }, top: 16, p: 2.5 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography level="title-md">Follow the money</Typography>
            <Chip
              size="sm"
              variant="soft"
              color={pending ? "neutral" : "success"}
            >
              {pending ? "Calculating…" : "Live calculation"}
            </Chip>
          </Stack>
          {!job && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
                my: 1,
              }}
            >
              {(["costs", "credits", "rep_rate"] as const).map((key) => (
                <FormControl key={key}>
                  <FormLabel>
                    {key === "rep_rate"
                      ? "Sample rep rate"
                      : key === "costs"
                        ? "Sample costs"
                        : "Sample credits"}
                  </FormLabel>
                  <Input
                    type="number"
                    value={sample[key]}
                    startDecorator={key !== "rep_rate" ? "$" : undefined}
                    endDecorator={key === "rep_rate" ? "%" : undefined}
                    slotProps={{
                      input: {
                        min: 0,
                        max: key === "rep_rate" ? 100 : 9999999999,
                        step: "0.01",
                      },
                    }}
                    onChange={(e) =>
                      setSample({ ...sample, [key]: e.target.value })
                    }
                  />
                </FormControl>
              ))}
              <Typography level="body-xs" sx={{ alignSelf: "center" }}>
                Sample only. Actual jobs use their recorded economics.
              </Typography>
            </Box>
          )}
          {result.isError && (
            <Alert color="danger">
              Check the rule names, amounts, and percentages. At least one sales
              rep rule is required. {result.error.message}
            </Alert>
          )}
          {result.data && !pending && !result.isError && (
            <CommissionBreakdown calculation={result.data} sample={!job} />
          )}
          {pending && (
            <Typography level="body-sm" sx={{ py: 4 }}>
              Updating the calculation…
            </Typography>
          )}
        </Card>
      </Box>
      {saveError && <Alert color="danger">{saveError}</Alert>}
      <Stack
        direction="row"
        gap={2}
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2 }}
      >
        <Typography level="body-sm" sx={{ maxWidth: 600 }}>
          {job
            ? "This override applies only to this job."
            : "Applies to jobs using the organization default."}{" "}
          Existing accrued or paid commissions keep their saved calculation.
        </Typography>
        <Stack direction="row" gap={1}>
          {onReset && (
            <Button
              variant="outlined"
              color="neutral"
              disabled={saving}
              onClick={onReset}
            >
              Use organization default
            </Button>
          )}
          <Button
            loading={saving}
            disabled={pending || !result.data || result.isError}
            onClick={() => onSave(structure)}
          >
            {job ? "Save job override" : "Save commission structure"}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
